import { type Node, type SortableListState } from '../types.js';
import { type SortableListViewAction } from './actions.js';
import * as actionTypes from './actionTypes.js';
import { arrayMove } from '@dnd-kit/sortable';

export const initialState = {
  activeSortId: null,
  disabled: false,
  isSorting: false,
  sortedData: null,
  sortedNodeIds: null,
};

export type SortableListViewReducer<T extends Node> = (
  state: SortableListState<T>,
  action: SortableListViewAction<T>,
) => SortableListState<T>;

export const sortableListViewReducer = <T extends Node>(
  state: SortableListState<T>,
  action: SortableListViewAction<T>,
): SortableListState<T> => {
  switch (action.type) {
    case actionTypes.CANCEL_DRAG: {
      return {
        ...state,
        activeSortId: null,
        isSorting: false,
      };
    }

    case actionTypes.DISABLE_SORTING: {
      return {
        ...state,
        disabled: true,
      };
    }

    case actionTypes.ENABLE_SORTING: {
      return {
        ...state,
        disabled: false,
      };
    }

    case actionTypes.END_DRAG: {
      if (state.disabled) {
        return state;
      }

      const { sortedData, sortedNodeIds } = state;
      const { active, over } = action.payload;

      let newSortedData = sortedData;
      let newSortedNodeIds = sortedNodeIds;

      const shouldUpdateOrder = over !== null && active.id !== over.id;
      if (sortedData && shouldUpdateOrder) {
        const oldIndex = sortedData.findIndex((item) => item.id === active.id);
        const newIndex = sortedData.findIndex((item) => item.id === over?.id);
        newSortedData = arrayMove([...sortedData], oldIndex, newIndex);
        newSortedNodeIds = newSortedData.map((item) => item.id);

        return {
          ...state,
          activeSortId: null,
          isSorting: false,
          sortedData: newSortedData,
          sortedNodeIds: newSortedNodeIds,
        };
      }

      return {
        ...state,
        activeSortId: null,
        isSorting: false,
      };
    }

    case actionTypes.LOAD_DATA: {
      const sortedData = action.payload;
      const sortedNodeIds = sortedData.map((node) => node.id);

      return {
        ...state,
        sortedData,
        sortedNodeIds,
      };
    }

    case actionTypes.START_DRAG: {
      if (state.disabled) {
        return state;
      }

      return {
        ...state,
        activeSortId: action.payload.id,
        isSorting: true,
      };
    }

    default:
      throw new Error('Needs a valid action');
  }
};
